import { useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import type { CronLog } from '../types'
import { CRON_STATUS_LABELS, CRON_STATUS_COLORS, TRIGGER_SOURCE_LABELS } from '../types'
import { CronEventsList } from './CronEventsList'

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('es-CL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function formatDuration(seconds: number): string {
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`
  return `${seconds.toFixed(1)}s`
}

interface MetricCardProps {
  label: string
  value: number
  subLabel?: string
  subValue?: number
}

function MetricCard({ label, value, subLabel, subValue }: MetricCardProps) {
  return (
    <div className="bg-white border rounded-lg p-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-semibold mt-1">{value}</p>
      {subLabel !== undefined && subValue !== undefined && (
        <p className="text-xs text-gray-400 mt-1">
          {subLabel}: {subValue}
        </p>
      )}
    </div>
  )
}

interface CronLogDetailProps {
  log: CronLog
}

export function CronLogDetail({ log }: CronLogDetailProps) {
  const navigate = useNavigate()
  const statusColors = CRON_STATUS_COLORS[log.status]
  const result = log.result

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/cron-logs')}
          className="text-gray-500 hover:text-gray-700 transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-semibold">
            Ejecución #{log.id}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {formatDate(log.started_at)}
          </p>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors.bg} ${statusColors.text}`}>
          {CRON_STATUS_LABELS[log.status]}
        </span>
      </div>

      {/* Info row */}
      <div className="bg-white border rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <p className="text-sm text-gray-500">Inicio</p>
          <p className="font-medium">{formatDate(log.started_at)}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Fin</p>
          <p className="font-medium">{formatDate(log.finished_at)}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Duración</p>
          <p className="font-medium">{formatDuration(log.duration_seconds)}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Origen</p>
          <p className="font-medium">{TRIGGER_SOURCE_LABELS[log.trigger_source] ?? log.trigger_source}</p>
        </div>
      </div>

      {/* Error message */}
      {log.error_message && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm font-medium text-red-800">Error fatal</p>
          <p className="text-sm text-red-700 mt-1">{log.error_message}</p>
        </div>
      )}

      {/* Metric cards */}
      {result && (
        <>
          <h2 className="text-lg font-semibold">Resultados</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <MetricCard
              label="Cumpleaños procesados"
              value={result.birthdays_processed}
              subLabel="Beneficios otorgados"
              subValue={result.birthdays_benefits_granted}
            />
            <MetricCard
              label="Recurrencias procesadas"
              value={result.recurring_processed}
              subLabel="Beneficios otorgados"
              subValue={result.recurring_benefits_granted}
            />
            <MetricCard
              label="Beneficios expirados"
              value={result.expired_benefits}
              subLabel="Vouchers expirados"
              subValue={result.expired_vouchers}
            />
            <MetricCard
              label="Notificaciones enviadas"
              value={result.expiring_notifications_sent}
            />
            <MetricCard
              label="Cambios de categoría"
              value={result.category_changes}
              subLabel="Upgrades"
              subValue={result.category_upgrades}
            />
            <MetricCard
              label="Downgrades"
              value={result.category_downgrades}
            />
            <MetricCard
              label="Beneficios cancelados (upgrade)"
              value={result.benefits_cancelled_upgrade}
            />
            <MetricCard
              label="Beneficios cancelados (downgrade)"
              value={result.benefits_cancelled_downgrade}
            />
          </div>

          {/* Errors list */}
          {result.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm font-medium text-red-800 mb-2">
                Errores durante la ejecución ({result.errors.length})
              </p>
              <ul className="space-y-1">
                {result.errors.map((err, i) => (
                  <li key={i} className="text-sm text-red-700 font-mono">
                    {err}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Granular events */}
      <h2 className="text-lg font-semibold">Eventos detallados</h2>
      <div className="bg-white border rounded-lg shadow-sm">
        <CronEventsList executionId={log.id} />
      </div>
    </div>
  )
}
